import React, { useState, useEffect, useRef } from "react";
import ColorField from "../components/ColorField";
import QRCode from "qrcode.react";
import { useTranslation } from "react-i18next";
import Sidebar from "./Sidebar";
import Header from "../components/Header";
import { useAuthState } from "react-firebase-hooks/auth";
import { getDatabase, ref, update } from "firebase/database";
import { getAuth } from "firebase/auth";
import { app } from "../firebase";
import { useNavigate } from "react-router-dom";
import {
  Download,
  Clock,
  PlayCircle,
  ExternalLink,
  Edit3,
  Save,
  X,
  Loader2,
  Check,
  Copy,
  QrCode,
  Info,
  CreditCard,
  Image as ImageIcon
} from "lucide-react";
import axios, { AxiosError } from "axios";
import { loginHeader } from "../utils/loginHeader";
import { applyStreamEdit } from "../utils/streamEdit";
import UpdatingAnimation from "../components/UpdatingAnimation";
import SuccessAnimation from "../components/SuccessAnimation";
import instructionManual from "../images/confcap-instructions.jpg";
import { trackClick } from "../utils/analytics";
import SecureQrCard from "../components/SecureQrCard";
import { useAdvancedSettings } from "../utils/advancedSettings";
import { useStreams } from "../hooks/useStreams";
import { activationTarget, activationUrl, ActivationTarget } from "../utils/activation";
import ActivationChooser from "../components/ActivationChooser";

interface OverviewProps {
  name: string;
  imageUrl: string;
  qrCodeData: string;
}

const Overview: React.FC<OverviewProps> = ({
  name: initialName,
  imageUrl: initialImageUrl,
  qrCodeData,
}) => {
  const { t } = useTranslation();
  const randomStreamName = getRandomStreamName();
  const auth = getAuth(app);
  const [user, loading, error] = useAuthState(auth);
  const { settings: adv } = useAdvancedSettings(user?.uid);
  const [streamData, setStreamData] = useState({
    uid: user?.uid,
    streamName: initialName,
    imageUrl: initialImageUrl,
    streamColor: "",
  });
  const [localError, setLocalError] = useState("");
  const [fetchedStreamData, setFetchedStreamData] = useState({
    uid: "",
    streamName: "",
    imageUrl: "",
    streamColor: "",
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isFirstVisit, setIsFirstVisit] = useState(false);
  const [hasNoStream, setHasNoStream] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const hasVisited = sessionStorage.getItem("hasVisitedOverview");
    if (!hasVisited) {
      setIsFirstVisit(true);
      sessionStorage.setItem("hasVisitedOverview", "true");
    }
  }, []);

  const { selected, streams, loading: streamsLoading, primaryName, select, reload } = useStreams();
  const [chooser, setChooser] = useState<ActivationTarget | null>(null);
  const activationHandled = useRef(false);

  useEffect(() => {
    if (!user || streamsLoading) return;
    if (selected) {
      setHasNoStream(false);
      const current = { uid: user.uid, streamName: selected.name, imageUrl: selected.imageUrl, streamColor: selected.color };
      setStreamData(current);
      setFetchedStreamData(current);
    } else {
      console.error("No data available for this user.");
      setHasNoStream(true);
    }
  }, [user, selected, streamsLoading]);

  // The app or the Chrome extension sent the person here to sign in: hand a stream back to it. With one stream that is automatic;
  // with several they choose which. The request is then removed from the address so switching streams later does not repeat it.
  useEffect(() => {
    if (!user || streamsLoading || activationHandled.current || streams.length === 0) return;
    const target = activationTarget(window.location.search);
    if (!target) return;
    activationHandled.current = true;
    window.history.replaceState(null, "", window.location.pathname + window.location.hash);
    if (streams.length === 1) window.location.href = activationUrl(target, streams[0].name, user.uid);
    else setChooser(target);
  }, [user, streams, streamsLoading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (error || (!user && !loading)) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-[2rem] shadow-xl text-center max-w-sm">
          <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center text-red-500 mx-auto mb-4">
            <Info size={32} />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Session Error</h2>
          <p className="text-slate-500 mb-6">{error?.message || "Please sign in to access your overview."}</p>
          <button onClick={() => navigate("/login")} className="btn-primary w-full">Sign In</button>
        </div>
      </div>
    );
  }

  if (hasNoStream) {
    return (
      <div className="flex flex-col h-screen bg-slate-50 font-inter overflow-hidden">
        <Header />
        <div className="flex flex-1 overflow-hidden">
          <Sidebar activeItem="overview" />
          <main className="flex-1 overflow-y-auto p-4 md:p-8 flex items-center justify-center">
            <div className="bg-white p-8 rounded-[2rem] shadow-xl text-center max-w-md">
              <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600 mx-auto mb-4">
                <Info size={32} />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">
                {t("overview.noStreamTitle", "You don't have a stream yet")}
              </h2>
              <p className="text-slate-500 mb-6">
                {t("overview.noStreamMessage", "Your stream wasn't created during sign-up, possibly because the page was refreshed. Create one now to get your stream link, QR code and settings.")}
              </p>
              <button onClick={() => navigate("/onboarding")} className="btn-primary w-full">
                {t("overview.noStreamButton", "Create my stream")}
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const handleEditDetails = () => {
    trackClick("button_click-edit_stream", streamData);
    setIsEditing(true);
  };

  const handleSaveDetails = async () => {
    const hasChanges =
      streamData.streamName !== fetchedStreamData.streamName ||
      streamData.imageUrl !== fetchedStreamData.imageUrl ||
      streamData.streamColor !== fetchedStreamData.streamColor;

    if (!hasChanges) {
      setIsEditing(false);
      return;
    }
    trackClick("button_click-edit_stream_save", streamData);
    setIsSaving(true);

    if (streamData.streamName !== fetchedStreamData.streamName && streamData.streamName !== "") {
      try {
        const validationResponse = await axios.get(`https://api.deafassistant.com/streamdesign/IsStreamNameAllowed?streamName=${streamData.streamName}`);
        if (validationResponse.status === 200 && validationResponse.data === false) {
          // Continue
        }
      } catch (error: any) {
        setIsSaving(false);
        setLocalError(error.response?.data || t("overview.streamNameError"));
        return;
      }
    }

    // Renames/restyles in place. Deleting first would erase the stream's saved transcripts and questions.
    try {
      await applyStreamEdit({
        oldName: fetchedStreamData.streamName,
        name: streamData.streamName === "" ? randomStreamName : streamData.streamName,
        logoUrl: streamData.imageUrl,
        bannerColor: streamData.streamColor,
        uid: auth.currentUser?.uid ?? "",
      });
    } catch (error) {
      setIsSaving(false);
      alert(t("errorCreatingStream") + " " + (error instanceof AxiosError ? error.response?.data : "") + ". " + t("errorCreatingStreamDescription"));
      return;
    }
    await addStreamDesign(
      streamData.streamName.toLowerCase(),
      streamData.imageUrl,
      streamData.streamColor
    );
    setIsEditing(false);
    setIsSaving(false);
    setShowSuccess(true);

    await new Promise((resolve) => setTimeout(resolve, 1500));
    setShowSuccess(false);
    resetAfterSave();

    const savedName = (streamData.streamName === "" ? randomStreamName : streamData.streamName).toLowerCase();
    // The account's profile holds its original stream; an additional stream exists only in the API.
    if (user && fetchedStreamData.streamName.toLowerCase() === (primaryName ?? "").toLowerCase()) {
      const db = getDatabase();
      const userRef = ref(db, `users/${user.uid}`);
      update(userRef, {
        uid: auth.currentUser?.uid,
        name: streamData.streamName === "" ? randomStreamName : streamData.streamName,
        imageLink: streamData.imageUrl === "" ? "https://cdn-icons-png.flaticon.com/512/2395/2395089.png" : streamData.imageUrl,
        color: streamData.streamColor === "" ? "#888888" : streamData.streamColor,
      })
        .then(() => {
          setFetchedStreamData({
            uid: auth.currentUser?.uid ?? "",
            streamName: streamData.streamName === "" ? randomStreamName : streamData.streamName,
            imageUrl: streamData.imageUrl === "" ? "https://cdn-icons-png.flaticon.com/512/2395/2395089.png" : streamData.imageUrl,
            streamColor: streamData.streamColor === "" ? "#888888" : streamData.streamColor,
          });
          trackClick("success-edit_stream_save");
        })
        .catch((error) => {
          console.error("Error saving details: ", error);
        });
    }
    await reload();
    select(savedName);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setStreamData({
      uid: fetchedStreamData.uid,
      streamName: fetchedStreamData.streamName,
      imageUrl: fetchedStreamData.imageUrl,
      streamColor: fetchedStreamData.streamColor,
    });
  };

  function getRandomStreamName() {
    return "stream" + Math.floor(Math.random() * 100000).toString();
  }

  const addStreamDesign = async (streamName: string, image: string, bannerColor: string) => {
    const apiUrl = "https://api.deafassistant.com/streamDesign/AddValues";
    const data = {
      streamName: streamName.toLowerCase(),
      uid: auth.currentUser?.uid,
      streamColor: bannerColor,
      logoUrl: image
    };
    try {
      axios.defaults.headers.post["Accept"] = "application/json; charset=utf-8";
      await axios.post(apiUrl, data, await loginHeader());
    } catch (error) {
      console.error("There was an error!", error);
      if (error instanceof AxiosError) {
        alert(t("errorCreatingStream") + " " + error.response?.data + ". " + t("errorCreatingStreamDescription"));
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const regex = /^[a-zA-Z0-9]*$/;
    if (name === "streamName" && !regex.test(value)) {
      setLocalError(t("overview.streamNameError"));
      return;
    } else {
      setLocalError("");
    }

    setStreamData((prevState) => ({ ...prevState, [name]: value }));
    const currentData = { ...streamData, [name]: value };
    const changesDetected =
      currentData.streamName !== fetchedStreamData.streamName ||
      currentData.imageUrl !== fetchedStreamData.imageUrl ||
      currentData.streamColor !== fetchedStreamData.streamColor;
    setHasChanges(changesDetected);
  };

  const resetAfterSave = () => {
    setHasChanges(false);
    setFetchedStreamData({
      uid: user?.uid ?? "",
      streamName: streamData.streamName,
      imageUrl: streamData.imageUrl,
      streamColor: streamData.streamColor,
    });
  };

  const handleDownload = () => {
    trackClick("button_click-download_manual");
    const link = document.body.appendChild(document.createElement("a"));
    link.href = instructionManual;
    link.download = "InstructionManual.jpeg";
    link.target = "_blank";
    link.click();
    window.open(instructionManual);
    document.body.removeChild(link);
  };

  const handleManageSubscription = () => {
    trackClick("button_click-manage_subscription");
    navigate("/billing");
  };

  const handleCopy = () => {
    const urlToCopy = `https://deafassistant.com/${encodeURIComponent(streamData.streamName.toLowerCase())}`;
    navigator.clipboard.writeText(urlToCopy)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => console.error("Copying to clipboard failed: ", err));
  };

  const videoCard = (
    <div className="bg-white rounded-[2rem] overflow-hidden shadow-lg border border-slate-100 mb-8 animate-in zoom-in duration-500">
      <div className="aspect-video w-full">
        <iframe
          title="vimeo-player"
          src="https://player.vimeo.com/video/1162461827?h=b87a1b867f"
          className="w-full h-full"
          frameBorder="0"
          referrerPolicy="strict-origin-when-cross-origin"
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
          allowFullScreen
        ></iframe>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col h-screen bg-slate-50 font-inter overflow-hidden">
      <Header />

      {chooser && user && (
        <ActivationChooser
          target={chooser}
          streams={streams}
          onPick={(name) => {
            window.location.href = activationUrl(chooser, name, user.uid);
            setChooser(null);
          }}
          onCancel={() => setChooser(null)}
        />
      )}

      {isSaving && (
        <div className="fixed inset-0 z-[100] flex justify-center items-center bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="flex flex-col justify-center items-center h-[40%] w-[40%]">
            <UpdatingAnimation />
          </div>
        </div>
      )}

      {showSuccess && (
        <div className="fixed inset-0 z-[100] flex justify-center items-center bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="flex flex-col justify-center items-center h-[40%] w-[40%]">
            <SuccessAnimation />
          </div>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        <Sidebar activeItem="overview" />

        <main className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">

            <header className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <h1 className="text-4xl font-bold text-slate-900 tracking-tight">
                  {t("overview.title")}
                </h1>
                <p className="text-slate-500 mt-1 text-lg">
                  {t("overview.subtitle")}
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                {!isEditing ? (
                  <>
                    {/* <button onClick={handleDownload} className="btn-secondary flex items-center gap-2">
                      <Download size={18} />
                      {t("overview.downloadManual")}
                    </button> */}
                    <button
                      onClick={() => {
                        trackClick("button_click-demo");
                        window.open("https://vimeo.com/1064921264", "_blank");
                      }}
                      className="btn-secondary flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-100"
                    >
                      <PlayCircle size={18} />
                      {t("common.demo")}
                    </button>
                    <button onClick={handleManageSubscription} className="btn-secondary flex items-center gap-2">
                      <CreditCard size={18} />
                      {t("overview.manageSubscription")}
                    </button>
                    <button onClick={handleEditDetails} className="btn-primary flex items-center gap-2">
                      <Edit3 size={18} />
                      {t("common.edit")}
                    </button>
                  </>
                ) : (
                  <div className="flex gap-3">
                    <button onClick={handleCancelEdit} className="btn-secondary flex items-center gap-2">
                      <X size={18} />
                      {t("common.cancel")}
                    </button>
                    <button
                      onClick={handleSaveDetails}
                      disabled={!hasChanges || isSaving}
                      className="btn-primary flex items-center gap-2 disabled:opacity-50"
                    >
                      {isSaving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                      {t("common.save")}
                    </button>
                  </div>
                )}
              </div>
            </header>

            <div className="grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-8">
                {isFirstVisit && videoCard}

                <div className="bg-white rounded-[2rem] p-8 shadow-sm border border-slate-100">
                  <div className="flex items-center gap-3 mb-8">
                    <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                      <Info size={20} />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900">Stream Configuration</h3>
                  </div>

                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="group">
                        <label className="block text-sm font-bold text-slate-500 mb-2 ml-1 transition-colors group-focus-within:text-blue-600">
                          {t("overview.streamName")}
                        </label>
                        <input
                          type="text"
                          value={streamData.streamName}
                          readOnly={!isEditing}
                          onChange={handleInputChange}
                          name="streamName"
                          className={`w-full bg-slate-50 border rounded-2xl py-3 px-4 outline-none transition-all ${isEditing
                            ? "border-slate-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                            : "border-transparent cursor-not-allowed text-slate-600"
                            } ${localError ? "border-red-500" : ""}`}
                        />
                        {localError && <p className="text-red-500 text-xs mt-1 ml-1">{localError}</p>}
                      </div>

                      <div className="group">
                        <label className="block text-sm font-bold text-slate-500 mb-2 ml-1">
                          {t("overview.imageUrl")}
                        </label>
                        <div className="relative">
                          <ImageIcon className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                          <input
                            type="text"
                            value={streamData.imageUrl}
                            readOnly={!isEditing}
                            onChange={handleInputChange}
                            name="imageUrl"
                            className={`w-full bg-slate-50 border rounded-2xl py-3 pl-12 pr-4 outline-none transition-all ${isEditing
                              ? "border-slate-200 focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                              : "border-transparent cursor-not-allowed text-slate-600"
                              }`}
                          />
                        </div>
                      </div>
                    </div>

                    <ColorField
                      id="stream-color"
                      label={t("overview.brandColor", "Brand color")}
                      placeholder={t("overview.brandColorPlaceholder", "Hex code, e.g. #000000. Your logo is shown on this color.")}
                      value={streamData.streamColor}
                      onChange={(value) => handleInputChange({ target: { name: "streamColor", value } } as React.ChangeEvent<HTMLInputElement>)}
                      readOnly={!isEditing}
                    />
                  </div>
                </div>
                {!isFirstVisit && videoCard}
              </div>

              <div className="lg:col-span-1 space-y-8">
                <div className="glass-card rounded-[2rem] p-8 overflow-hidden relative group">
                  <div className="flex flex-col items-center text-center space-y-6 relative z-10">
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-xs font-bold uppercase tracking-wider">
                      <QrCode size={14} />
                      {t("overview.qrCodeTitle")}
                    </div>

                    <div className="bg-white p-6 rounded-[2.5rem] shadow-xl shadow-blue-500/10 hover:scale-105 transition-transform duration-500 cursor-pointer" onClick={handleCopy}>
                      <QRCode
                        value={`https://deafassistant.com/${encodeURIComponent(streamData.streamName.toLowerCase())}`}
                        size={200}
                        fgColor={streamData.streamColor || "#1e293b"}
                        level="H"
                      />
                    </div>

                    <div className="space-y-4 w-full">
                      <p className="text-slate-500 text-sm">
                        {t("overview.qrCodeSubtitle")}
                      </p>

                      <button
                        onClick={handleCopy}
                        className={`w-full py-4 rounded-2xl font-bold border flex items-center justify-center gap-2 transition-all ${copied
                          ? "bg-green-50 border-green-200 text-green-600"
                          : "bg-white border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-600 shadow-sm"
                          }`}
                      >
                        {copied ? <Check size={20} /> : <Copy size={20} />}
                        {copied ? t("overview.copiedToClipboard") : "Copy Stream Link"}
                      </button>

                      <a
                        href={`https://deafassistant.com/${encodeURIComponent(streamData.streamName.toLowerCase())}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 text-blue-600 hover:text-blue-700 font-bold text-sm group"
                      >
                        <ExternalLink size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                        {`deafassistant.com/${streamData.streamName.toLowerCase()}`}
                      </a>
                    </div>
                  </div>
                  <div className="absolute -right-12 -bottom-12 w-48 h-48 bg-blue-500/5 rounded-full blur-3xl group-hover:bg-blue-500/10 transition-colors duration-700" />
                </div>
                {adv.secureQr && user && streamData.streamName && (
                  <SecureQrCard streamName={streamData.streamName} uid={user.uid} color={streamData.streamColor} />
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Overview;
